// Il token di sessione vive in due posti del client:
// - il cookie `token${base_progetto}` dell'origine della pagina, che leggono il middleware
//   `auth.js` e le pagine (`useCookie`, header Authorization);
// - IndexedDB, chiave `token`: la copia che sopravvive ai riavvii.
// Sul web il cookie lo scrive il server; nell'app nativa (WebView su capacitor://localhost) il
// cookie del server non arriva alla pagina, quindi il token viaggia nel corpo delle risposte e
// lo scrive il client con `salvaToken`.
import { nextTick } from "vue";
import { deleteValuesFromIndexedDB, setValuesInIndexedDB } from "./idb.js";

const nomeCookieToken = () => `token${useRuntimeConfig().public.base_progetto}`;

/**
 * Legge un cookie da una stringa `document.cookie` (pura): `nome=valore; altro=…`. Vale la
 * prima occorrenza, il valore è decodificato; `null` se manca o è vuoto. Serve a leggere il
 * token nel momento della richiesta: il ref di `useCookie` può essere in ritardo subito
 * dopo `salvaToken`.
 */
export function tokenDaCookieDocumento(cookieString, nome) {
  for (const parte of String(cookieString ?? "").split(";")) {
    const i = parte.indexOf("=");
    if (i < 0 || parte.slice(0, i).trim() !== nome) continue;
    const grezzo = parte.slice(i + 1).trim();
    try {
      return decodeURIComponent(grezzo) || null;
    } catch {
      return grezzo || null;
    }
  }
  return null;
}

/**
 * Allinea cookie e IndexedDB (pura: `cookie` e `idb` sono `{ get(), set(v) }`, che possono
 * restituire promesse). Se il token c'è solo in IndexedDB scrive il cookie, se c'è solo nel
 * cookie scrive IndexedDB; se c'è in tutti e due o in nessuno non scrive. Si risolve dopo
 * la scrittura, con il token risultante (quello del cookie se ci sono tutti e due) o `null`.
 */
export async function sincronizzaTokenLocale({ cookie, idb }) {
  const daCookie = (await cookie.get()) || null;
  const daIdb = (await idb.get()) || null;

  if (daIdb && !daCookie) {
    await cookie.set(daIdb);
    return daIdb;
  }
  if (daCookie && !daIdb) {
    await idb.set(daCookie);
    return daCookie;
  }
  return daCookie;
}

// `useCookie` scrive document.cookie al flush del suo watcher: `nextTick` lo aspetta, così chi
// legge il cookie subito dopo (la richiesta successiva, il middleware) trova il valore nuovo.
export async function salvaToken(token) {
  if (!token) return;
  useCookie(nomeCookieToken()).value = token;
  await nextTick();
  await setValuesInIndexedDB({ token });
}

export async function cancellaToken() {
  useCookie(nomeCookieToken()).value = null;
  await nextTick();
  await deleteValuesFromIndexedDB(["token"]);
}
